import { useMemo } from 'react'

import { useModals, useNumericParams, useSetState } from '@domain-group/react-hooks'

import { ModalContext } from '@domain-group/suit-theme'

import PageContext from '@contexts/pageContext'

import handleCallbackAction from '@functions/handleCallbackAction'

import CreateCompatibleProductModal from '@modals/CreateCompatibleProductModal'
import DuplicateServiceProductModal from '@modals/DuplicateServiceProductModal'
import EditProductTaskSettingsModal from '@modals/EditProductTaskSettingsModal'
import ManageServiceProductPublicationsModal from '@modals/ManageServiceProductPublicationsModal'

import PageContent from './components/PageContent'

const callbacks = (component, setState) => {
  const componentCallbacks = {
    CreateCompatibleProductModal: {
      closeModal: () => setState({ showCreateCompatibleProductModal: false }),
      updateServiceProduct: payload => handleCallbackAction(payload),
    },
    DuplicateServiceProductModal: {
      closeModal: () => setState({ showDuplicateServiceProductModal: false }),
      duplicateServiceProduct: payload => handleCallbackAction(payload),
    },
    EditProductTaskSettingsModal: {
      closeModal: () => setState({ showEditProductTaskSettingsModal: false }),
      updateServiceProduct: payload => handleCallbackAction(payload),
    },
    ManageServiceProductPublicationsModal: {
      closeModal: () => setState({ showManageServiceProductPublicationsModal: false }),
      updateServiceProduct: payload => handleCallbackAction(payload),
    },
  }

  return componentCallbacks[component] || {}
}

const defaultState = {
  showCreateCompatibleProductModal: false,
  showDuplicateServiceProductModal: false,
  showEditProductTaskSettingsModal: false,
  showManageServiceProductPublicationsModal: false,
}

const Relations = () => {
  const [state, setState] = useSetState(defaultState)
  const {
    showCreateCompatibleProductModal,
    showDuplicateServiceProductModal,
    showEditProductTaskSettingsModal,
    showManageServiceProductPublicationsModal,
  } = state

  const { serviceProductId } = useNumericParams()

  const modalContext = useModals({ setState })
  const {
    callbacks: { setModalData },
  } = modalContext

  const pageContext = useMemo(() => ({
    callbacks: {
      showCreateCompatibleProductModal: (payload) => {
        setModalData('CreateCompatibleProductModal', payload)
        setState({ showCreateCompatibleProductModal: true })
      },
      showDuplicateServiceProductModal: (payload) => {
        setModalData('DuplicateServiceProductModal', payload)
        setState({ showDuplicateServiceProductModal: true })
      },
      showEditProductTaskSettingsModal: (payload) => {
        setModalData('EditProductTaskSettingsModal', payload)
        setState({ showEditProductTaskSettingsModal: true })
      },
      showManageServiceProductPublicationsModal: (payload) => {
        setModalData('ManageServiceProductPublicationsModal', payload)
        setState({ showManageServiceProductPublicationsModal: true })
      },
      updateServiceProduct: payload => handleCallbackAction(payload),
    },
  }), [])

  return (
    <PageContext.Provider value={pageContext}>
      <ModalContext.Provider value={modalContext}>
        <PageContent serviceProductId={serviceProductId} />

        <CreateCompatibleProductModal
          callbacks={callbacks('CreateCompatibleProductModal', setState)}
          showModal={showCreateCompatibleProductModal}
        />

        <DuplicateServiceProductModal
          callbacks={callbacks('DuplicateServiceProductModal', setState)}
          showModal={showDuplicateServiceProductModal}
        />

        <EditProductTaskSettingsModal
          callbacks={callbacks('EditProductTaskSettingsModal', setState)}
          showModal={showEditProductTaskSettingsModal}
        />

        <ManageServiceProductPublicationsModal
          callbacks={callbacks('ManageServiceProductPublicationsModal', setState)}
          showModal={showManageServiceProductPublicationsModal}
        />
      </ModalContext.Provider>
    </PageContext.Provider>
  )
}

export default Relations
